/* Imports */
// Library
import {
  rollups, range, format, sum, scaleLinear, extent, scaleOrdinal
} from 'd3'
import { CONFIG, SLIDE_METRICS } from './constants'

const allVals = (data, control) => Object.values(data[control]).reduce(
  (t, v) => {
    return {
      ...t,
      control: [...(t.control || []), ...v.control],
      treatment: [...(t.treatment || []), ...v.treatment]
    };
  },
  {}
)

const generateDataVals = (allData, control) => {
  const data = allVals(allData, control)

  // get values that do not change between control and treatment
  const remainders = range(11).flatMap((d) => {
    const orig =
      data.control.filter(({ num }) => num === d).length >=
        data.treatment.filter(({ num }) => num === d).length
        ? data.control.filter(({ num }) => num === d).length
        : data.treatment.filter(({ num }) => num === d).length;
    return (
      orig -
      Math.abs(
        data.control.filter(({ num }) => num === d).length -
        data.treatment.filter(({ num }) => num === d).length
      )
    );
  });

  /* Values that change between control and treament */
  // map total number of scores for control
  const controlTotals =
    range(11)
      .map((d, _) => data.control.filter(({ num }) => num === d).length);

  const controlTotalsVals = controlTotals.flatMap((tVal, i) =>

    range(tVal - remainders[i] > 0 ? tVal - remainders[i] : 0)
      .map((num) => i)
  );
  // map total number of scores for treatment
  const treatmentTotals =
    range(11)
      .map((d, _, arr) => data.treatment.filter(({ num }) => num === d).length);
  const treatmentTotalsVals = treatmentTotals.flatMap((tVal, i) =>

    range(tVal - remainders[i] > 0 ? tVal - remainders[i] : 0)
      .map((num) => i)
  );
  // create array of objects with control and treatment values
  const transitioningDataValues = controlTotalsVals.map((cValue, i) => ({
    control: cValue, // control value at that index
    treatment: treatmentTotalsVals[i] // treatment value at the same index
  }));
  /* Values that do not change between control and treament */
  // create array of objects with control and treatment values
  const remainderVals = remainders.flatMap((d, i) => range(d).map((_) => i));
  const remainingDataValues = remainderVals.map((value) => ({
    control: value,
    treatment: value
  }));

  /* All Values */
  // combine arrays of values that change between control and treament and that do not
  const dataValues = [...remainingDataValues, ...transitioningDataValues].map(
    (v, i) => ({
      ...v,
      id: i
      // party
    })
  );
  return dataValues;
}

const getAverages = (data, currentColumn, control) => Object.entries(data[control])
  .filter(([party]) => CONFIG.COLOR_DOMAIN.includes(party))
  .map(
    ([party, pData]) => {
      return {
        mean: +format(",.2f")(
          sum(pData[currentColumn], (d) => d.num) / pData[currentColumn].length
        ),
        party
      };
    }
  )

const getAreaData = (data, currentColumn, control) => rollups(
  generateDataVals(data, control),
  (v) => v.length,
  (d) => d[currentColumn]
)

// yScale helper function
function getIndex(d, data, currentColumn) {
  // filter data for current column data only
  const bandArray = data.filter((f) => f[currentColumn] === d[currentColumn]);
  // find index of data point in new filtered data
  return bandArray.findIndex((f) => f.id === d.id);
}


/* Data transformations for Slide Chart */

const summaryData = (data) => data.map(d => {
  SLIDE_METRICS.forEach(m => {
    d[`${m.name}Diff`] = d[m.treatment] - d[m.control];
  });
  return d;
})

const rangeScale = (data) => {
  // figure out differences between control and treatment
  const [diffMin, diffMax] = extent(summaryData(data).map(d => SLIDE_METRICS.map(m => d[`${m.name}Diff`])).reduce((a, v) => a.concat(v), []));
  const denom = 10;
  const diffDomain = [Math.floor(diffMin * denom) / denom, Math.ceil(diffMax * denom) / denom];
  return scaleLinear()
    .domain(diffDomain)
    .range([1, 99]);
}

const mappedData = (data) => summaryData(data).map(({ statement, domesticDiff, internationalDiff }) => ({
  statement,
  data: [
    {
      type: "domestic",
      domesticDiff
    },
    {
      type: "international",
      internationalDiff
    },
  ]
}))

// color scale
const colors = scaleOrdinal()
  .domain(CONFIG.COLOR_DOMAIN)
  .range(CONFIG.COLOR_RANGE)


export { getIndex, getAverages, getAreaData, generateDataVals, mappedData, rangeScale, colors }